import axiosInstances from 'src/utils/axiosInstances';
import {
  FIVE9_CALL_API,
  five9CallPath,
  five9Click2DialRegisterPath,
  five9DispatchLinkPath
} from 'src/constants/five9CallApi';

const REQUEST_TIMEOUT_MS = 10000;
const MAX_ATTEMPTS = 3;
const INITIAL_RETRY_DELAY_MS = 250;

const dispatchQoreAxios = axiosInstances({
  baseURL: process.env.REACT_APP_DISPATCH_QORE_API,
  headers: {
    'Ocp-Apim-Subscription-Key': process.env.REACT_APP_NSD_API_KEY,
  },
  withCredentials: true,
});

export const isRetryableFive9CallError = (error) => {
  if (error?.response?.status === 503) return true;
  return !error?.response && (
    error?.code === 'ECONNABORTED'
    || error?.code === 'ETIMEDOUT'
    || /\btimeout\b/i.test(error?.message || '')
  );
};

const wait = (delayMs) => new Promise((resolve) => {
  setTimeout(resolve, delayMs);
});

export const executeFive9CallOperation = async (
  operation,
  {
    client = dispatchQoreAxios,
    sleep = wait,
    maxAttempts = MAX_ATTEMPTS,
    initialDelayMs = INITIAL_RETRY_DELAY_MS
  } = {}
) => {
  const runAttempt = async (attempt) => {
    try {
      return await client.request({
        method: operation.method,
        url: operation.path,
        data: operation.body,
        timeout: REQUEST_TIMEOUT_MS
      });
    } catch (error) {
      if (!isRetryableFive9CallError(error) || attempt >= maxAttempts) throw error;
      await sleep(initialDelayMs * (2 ** (attempt - 1)));
      return runAttempt(attempt + 1);
    }
  };
  return runAttempt(1);
};

export const getFive9Call = (five9SessionId, options = {}) => (
  executeFive9CallOperation({
    method: 'GET',
    path: five9CallPath(five9SessionId)
  }, options)
);

/**
 * Register an outbound click-to-dial call against the dispatch it was
 * placed from: upserts the call, attaches it to the dispatch and
 * pre-registers its CallTranscriptsRecord.
 *
 * @param {{ dispatchId: number, five9SessionId: string, five9InteractionId: string,
 *           five9CallId: string, dialedNumber: string }} payload
 * @param {Object} [options]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const registerClick2DialCall = (payload, options = {}) => (
  executeFive9CallOperation({
    method: 'POST',
    path: five9Click2DialRegisterPath(),
    body: payload
  }, options)
);

/**
 * Link a call to an existing dispatch the agent explicitly chose (e.g. an
 * inbound caller on a dispatch found by search): upserts the call, attaches
 * it to the dispatch and pre-registers its CallTranscriptsRecord.
 *
 * @param {string} five9SessionId
 * @param {{ dispatchId: number, five9InteractionId: string, five9CallId: string,
 *           direction?: string }} payload
 * @param {Object} [options]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const linkCallToDispatch = (five9SessionId, payload, options = {}) => (
  executeFive9CallOperation({
    method: 'POST',
    path: five9DispatchLinkPath(five9SessionId),
    body: payload
  }, options)
);

export const sendFive9CallKeepalive = (operation, {
  fetchImpl = typeof fetch === 'function' ? fetch : null,
  baseUrl = process.env.REACT_APP_DISPATCH_QORE_API
} = {}) => {
  if (!fetchImpl || !baseUrl) return false;
  const url = `${String(baseUrl).replace(/\/+$/, '')}/${String(operation.path).replace(/^\/+/, '')}`;
  try {
    fetchImpl(url, {
      method: operation.method,
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': process.env.REACT_APP_NSD_API_KEY,
      },
      credentials: 'include',
      keepalive: true,
      body: operation.method === 'GET' ? undefined : JSON.stringify(operation.body)
    });
    return true;
  } catch (_) {
    return false;
  }
};

// The queue (see five9CallQueue.js) owns retry/backoff bookkeeping for its
// own queued operations and reconciliation GETs — it counts each
// service call as one "attempt" toward its own bounded cap (8). Without
// this, `executeFive9CallOperation`'s own internal retry (up to 3 HTTP
// requests per call, its default for other/direct callers — see the
// named export used in isolation by five9CallsService.test.js) would
// silently multiply real network traffic during an outage well beyond
// the queue's documented cap (up to 24 real requests instead of 8).
// `maxAttempts: 1` here makes the queue's object-form service the single
// source of truth for retry attempts; direct callers of the named
// `executeFive9CallOperation`/`getFive9Call` exports are unaffected.
export const five9CallsService = {
  execute: (operation, options = {}) => (
    executeFive9CallOperation(operation, { maxAttempts: 1, ...options })
  ),
  get: (five9SessionId, options = {}) => (
    getFive9Call(five9SessionId, { maxAttempts: 1, ...options })
  ),
  keepalive: sendFive9CallKeepalive,
  callsPath: FIVE9_CALL_API.callsPath
};
